import { defineStore } from 'pinia';
import { api } from 'src/boot/api';
import { devError } from '@shared/vue/utils/debug';
import type { StaffPermissionSlug } from 'src/types/permissions';

export const USER_STORE_ID = 'store-user';

export interface User {
  uuid: string;
  email: string;
  name: string;
  first_name?: string | undefined;
  last_name?: string | undefined;
  avatar_url?: string | undefined;
  system_role?: string | undefined;
  system_permissions?: string[] | undefined;
  created_at?: string | undefined;
  updated_at?: string | undefined;
}

interface UserState {
  data: User | null;
  isLoaded: boolean;
  isLoading: boolean;
  error: string | null;
}

export const useUserStore = defineStore(USER_STORE_ID, {
  state: (): UserState => ({
    data: null,
    isLoaded: false,
    isLoading: false,
    error: null,
  }),

  getters: {
    getUuid: (state) => state.data?.uuid || '',
    getEmail: (state) => state.data?.email || '',
    getName: (state) => state.data?.name || '',
    getFirstName: (state) => state.data?.first_name || '',
    getLastName: (state) => state.data?.last_name || '',
    getAvatarUrl: (state) => state.data?.avatar_url || '',
    getSystemRole: (state) => state.data?.system_role || '',
    getPermissions: (state) => state.data?.system_permissions || [],

    getInitials: (state) => {
      if (!state.data?.name) return '?';
      const names = state.data.name.split(' ').filter((n) => n.length > 0);
      const firstName = names[0];
      const lastName = names[names.length - 1];
      if (names.length >= 2 && firstName && lastName) {
        return `${firstName[0]}${lastName[0]}`.toUpperCase();
      }
      return state.data.name.substring(0, 2).toUpperCase();
    },

    /**
     * Check if user is staff (has staff system role)
     * This is the basic check for accessing the SI
     */
    isStaff: (state) => state.data?.system_role === 'staff',

    /**
     * Check if user has a specific permission. The slug parameter is
     * typed against StaffPermissionSlug — typos and dead slugs (e.g.
     * checking a name the seeder doesn't seed) fail at compile time.
     * Usage: userStore.hasPermission('staff.tickets.view')
     */
    hasPermission: (state) => (permission: StaffPermissionSlug): boolean => {
      return state.data?.system_permissions?.includes(permission) || false;
    },

    /**
     * Check if user has any of the given permissions
     * Usage: userStore.hasAnyPermission(['staff.tickets.view', 'staff.tickets.create'])
     */
    hasAnyPermission: (state) => (permissions: StaffPermissionSlug[]): boolean => {
      if (!state.data?.system_permissions) return false;
      return permissions.some((p) => state.data?.system_permissions?.includes(p));
    },

    /**
     * Check if user has all of the given permissions
     * Usage: userStore.hasAllPermissions(['staff.tickets.view', 'staff.tickets.respond'])
     */
    hasAllPermissions: (state) => (permissions: StaffPermissionSlug[]): boolean => {
      if (!state.data?.system_permissions) return false;
      return permissions.every((p) => state.data?.system_permissions?.includes(p));
    },
  },

  actions: {
    setUser(user: User) {
      this.data = user;
      this.isLoaded = true;
      this.error = null;
    },

    clearData() {
      this.data = null;
      this.isLoaded = false;
      this.error = null;
    },

    setLoading(isLoading: boolean) {
      this.isLoading = isLoading;
    },

    setError(error: string | null) {
      this.error = error;
    },

    /**
     * Fetch user profile from API
     * Used after login and session restoration
     */
    async fetchUser(): Promise<boolean> {
      this.isLoading = true;
      this.error = null;

      try {
        const response = await api.get('/account/personal');
        // API response structure: { status, data: { section, data: { identity, account, avatar } } }
        const personalData = response.data?.data?.data;
        if (!personalData?.identity) {
          this.error = 'Failed to load account. Please try again.';
          return false;
        }
        const identity = personalData.identity;
        const avatar = personalData.avatar;

        // Check if user has staff role
        if (identity.system_role !== 'staff') {
          this.error = 'Access denied. Staff account required.';
          return false;
        }

        this.data = {
          uuid: identity.uuid,
          email: identity.email,
          name: identity.name,
          first_name: identity.first_name,
          last_name: identity.last_name,
          avatar_url: avatar?.url || undefined,
          system_role: identity.system_role,
          system_permissions: identity.system_permissions || [],
        };
        this.isLoaded = true;

        return true;
      } catch (error) {
        devError('Failed to fetch user profile:', error);
        this.error = 'Failed to load user profile';
        return false;
      } finally {
        this.isLoading = false;
      }
    },
  },

  persist: {
    // Persist only `data` (so the cached profile paints instantly on reload),
    // NOT `isLoaded`. The auth-restore boot file fetches a fresh profile only
    // when `!userStore.isLoaded`; persisting `isLoaded === true` made that
    // guard skip the fetch on every reload, so a staff member kept stale
    // `system_permissions` / `system_role` (e.g. after a server-side revoke)
    // until an explicit logout. Letting `isLoaded` reset to false on cold boot
    // forces the re-validating fetch while still showing the cached profile.
    pick: ['data'],
  },
});
